import { computed, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseApiService } from './base-api.service';
import {
  JourneyDto,
  FareModel,
  SeatPreferences,
  FoundReservationBookingResponseModel,
  ProvisionalReservationResponseModel,
} from '@models';
import { environment } from '../../../environments/environment';

export interface UpdateBookingStatusRequest {
  status: 'CONFIRM' | 'CANCEL';
}

export interface SeatProperty {
  code: string;
  description: string;
}

export interface CarrierPolicy {
  carrierCode: string;
  validFrom: string;
  validUntil: string;
  seatProperties: SeatProperty[];
}

@Injectable({
  providedIn: 'root',
})
export class ReservationService extends BaseApiService {
  override baseUrl = environment.reservationServerUrl;

  public readonly selectedOutwardJourney = signal<JourneyDto | null>(null);

  public readonly selectedReturnJourney = signal<JourneyDto | null>(null);

  public readonly reservationFound = signal<FoundReservationBookingResponseModel | null>(null);

  public readonly provisionalReservationResponse =
    signal<ProvisionalReservationResponseModel | null>(null);
  // No añadimos "type" al JourneyDto original porque
  // puede romper los radio buttons de Journeys.
  public readonly selectedOutwardWithType = computed<JourneyDto | null>(() => {
    const journey = this.selectedOutwardJourney();

    if (!journey) {
      return null;
    }

    return {
      ...journey,
      type: 'Outward',
    };
  });

  public readonly selectedReturnWithType = computed<JourneyDto | null>(() => {
    const journey = this.selectedReturnJourney();

    if (!journey) {
      return null;
    }

    return {
      ...journey,
      type: 'Return',
    };
  });

  public readonly selectedFares = signal<FareModel[]>([]);
  public readonly defaultSeatPreferences: SeatPreferences[] = [
    {
      coachType: 'Any',
      direction: 'Any',
      nearLuggageRack: false,
      nearToilets: false,
      position: 'Any',
      powerSocket: false,
      table: false,
    },
  ];
  public readonly seatPreferences = signal<SeatPreferences[]>([]);
  public readonly bikesValue = signal<number[]>([0, 0]);

  public clearReservation(): void {
    this.selectedOutwardJourney.set(null);
    this.selectedReturnJourney.set(null);
    this.selectedFares.set([]);
    this.seatPreferences.set([]);
    this.bikesValue.set([0, 0]);
    this.reservationFound.set(null);
    this.provisionalReservationResponse.set(null);
  }

  /**
   * Crea una reserva provisional.
   *
   * POST /v2/api/bookings/makeProvisionalReservation
   */
  public postCreateProvisionalBooking(
    request: unknown,
  ): Observable<ProvisionalReservationResponseModel> {
    return this.post<ProvisionalReservationResponseModel>(
      '/bookings/makeProvisionalReservation',
      request,
    );
  }

  /**
   * Recupera una reserva existente.
   *
   * GET /v2/api/bookings/{bookingReference}
   */
  public getBooking(
    bookingReference: string,
  ): Observable<{ data: FoundReservationBookingResponseModel }> {
    return this.get<{ data: FoundReservationBookingResponseModel }>(
      `/bookings/${bookingReference}`,
    );
  }

  /**
   * Actualiza el estado de una reserva.
   *
   * CONFIRM:
   * PUT /v2/api/bookings/{bookingReference}/status
   * { "status": "CONFIRM" }
   *
   * CANCEL:
   * { "status": "CANCEL" }
   */
  public updateBookingStatus(
    bookingReference: string,
    request: UpdateBookingStatusRequest,
  ): Observable<unknown> {
    return this.put<unknown>(`/bookings/${bookingReference}/status`, request);
  }

  /**
   * Cancela una reserva utilizando el endpoint específico
   * disponible en RARS.
   *
   * PUT /v2/api/bookings/{bookingReference}/cancel
   */
  public cancelBooking(bookingReference: string): Observable<unknown> {
    return this.put<unknown>(`/bookings/${bookingReference}/cancel`, {});
  }

  /**
   * Obtiene las propiedades de asiento disponibles.
   *
   * GET /v2/api/refdata/seatProperties
   */
  public getSeatProperties(): Observable<SeatProperty[]> {
    return this.get<SeatProperty[]>('/refdata/seatProperties');
  }
}
